import Phaser from 'phaser';
import { GAME } from '../config';
import type { Item } from './levels';

// Panel rotation no longer affects output — output comes from the roof face.
// Rotation just swaps a 1×2 panel between portrait and landscape footprints.
export type PanelInstance = {
  type: Item;
  cellX: number;
  cellY: number;
  facingDeg: number; // 0 / 90 / 180 / 270 — 90/270 swap span dims
  spanW(): number;
  spanH(): number;
};

export function makePanel(
  type: Item,
  cellX: number,
  cellY: number,
  facingDeg = 180,
): PanelInstance {
  const inst: PanelInstance = {
    type,
    cellX,
    cellY,
    facingDeg,
    spanW() {
      return inst.facingDeg % 180 === 0 ? type.width : type.height;
    },
    spanH() {
      return inst.facingDeg % 180 === 0 ? type.height : type.width;
    },
  };
  return inst;
}

export function panelOccupiedCells(p: PanelInstance): string[] {
  const cells: string[] = [];
  for (let dy = 0; dy < p.spanH(); dy++) {
    for (let dx = 0; dx < p.spanW(); dx++) {
      cells.push(`${p.cellX + dx},${p.cellY + dy}`);
    }
  }
  return cells;
}

/**
 * Renders any roof-mounted item (panel / inverter / battery) at world
 * coordinates derived from `origin` and the item's grid position.
 */
export class PanelSprite {
  scene: Phaser.Scene;
  panel: PanelInstance;
  origin: { x: number; y: number };
  container!: Phaser.GameObjects.Container;
  body!: Phaser.GameObjects.Graphics;
  detail!: Phaser.GameObjects.Graphics;

  constructor(
    scene: Phaser.Scene,
    panel: PanelInstance,
    origin: { x: number; y: number },
  ) {
    this.scene = scene;
    this.panel = panel;
    this.origin = origin;
  }

  draw() {
    if (!this.container) {
      this.container = this.scene.add.container(0, 0);
      this.body = this.scene.add.graphics();
      this.detail = this.scene.add.graphics();
      this.container.add([this.body, this.detail]);
    }
    this.refresh();
  }

  refresh() {
    PanelSprite.render(
      this.body,
      this.detail,
      this.panel.type,
      this.origin.x + this.panel.cellX * GAME.cell,
      this.origin.y + this.panel.cellY * GAME.cell,
      this.panel.spanW() * GAME.cell,
      this.panel.spanH() * GAME.cell,
      this.panel.facingDeg,
    );
  }

  destroy() {
    this.container?.destroy();
  }

  static render(
    body: Phaser.GameObjects.Graphics,
    detail: Phaser.GameObjects.Graphics | null,
    item: Item,
    px: number,
    py: number,
    w: number,
    h: number,
    facingDeg: number,
  ) {
    body.clear();
    detail?.clear();
    if (item.role === 'panel') {
      PanelSprite.renderPanel(body, detail, item, px, py, w, h, facingDeg);
    } else if (item.role === 'inverter') {
      PanelSprite.renderInverter(body, detail, item, px, py, w, h);
    } else {
      PanelSprite.renderBattery(body, detail, item, px, py, w, h);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // PV PANEL
  // ────────────────────────────────────────────────────────────────────────────
  private static renderPanel(
    body: Phaser.GameObjects.Graphics,
    detail: Phaser.GameObjects.Graphics | null,
    item: Item,
    px: number,
    py: number,
    w: number,
    h: number,
    facingDeg: number,
  ) {
    body.fillStyle(0x000000, 0.45);
    body.fillRoundedRect(px + 2, py + 4, w, h, 6);

    body.fillStyle(item.frame, 1);
    body.fillRoundedRect(px, py, w, h, 6);
    body.lineStyle(1, 0xffffff, 0.18);
    body.strokeRoundedRect(px + 1, py + 1, w - 2, h - 2, 5);

    const framePad = Math.max(3, Math.min(w, h) * 0.07);
    const sx = px + framePad;
    const sy = py + framePad;
    const sw = w - framePad * 2;
    const sh = h - framePad * 2;
    body.fillStyle(item.silicon, 1);
    body.fillRoundedRect(sx, sy, sw, sh, 3);

    const aspect = w / h;
    const longAxisHorizontal = aspect > 1;
    const cellsLong = longAxisHorizontal ? 8 : 4;
    const cellsShort = longAxisHorizontal ? 4 : 8;
    const cols = longAxisHorizontal ? cellsLong : cellsShort;
    const rows = longAxisHorizontal ? cellsShort : cellsLong;
    const cellGap = 1.5;
    const cellW = (sw - cellGap * (cols + 1)) / cols;
    const cellH = (sh - cellGap * (rows + 1)) / rows;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cellX = sx + cellGap + c * (cellW + cellGap);
        const cellY = sy + cellGap + r * (cellH + cellGap);
        body.fillStyle(PanelSprite.lighten(item.silicon, 0.08), 1);
        body.fillRect(cellX, cellY, cellW, cellH);
        body.fillStyle(0xffffff, 0.06);
        body.fillRect(cellX, cellY, cellW, cellH * 0.45);
      }
    }
    body.lineStyle(0.6, 0x9ab0d0, 0.5);
    if (longAxisHorizontal) {
      for (let r = 1; r < rows; r++) {
        const yy = sy + cellGap + r * (cellH + cellGap) - cellGap / 2;
        body.lineBetween(sx + 2, yy, sx + sw - 2, yy);
      }
    } else {
      for (let c = 1; c < cols; c++) {
        const xx = sx + cellGap + c * (cellW + cellGap) - cellGap / 2;
        body.lineBetween(xx, sy + 2, xx, sy + sh - 2);
      }
    }
    body.fillStyle(0xffffff, 0.04);
    body.fillTriangle(sx, sy, sx + sw * 0.55, sy, sx, sy + sh * 0.55);

    if (detail) {
      const isPortrait = h >= w;
      const boxLen = (isPortrait ? w : h) * 0.32;
      const boxThick = 4;
      let bx, by, bw, bh;
      if (isPortrait) {
        bx = px + (w - boxLen) / 2;
        by = py + h * 0.62;
        bw = boxLen;
        bh = boxThick;
      } else {
        bx = px + w * 0.62;
        by = py + (h - boxLen) / 2;
        bw = boxThick;
        bh = boxLen;
      }
      detail.fillStyle(0x1a1f2a, 0.85);
      detail.fillRect(bx, by, bw, bh);
      detail.lineStyle(0.5, 0x3a4250, 0.6);
      detail.strokeRect(bx, by, bw, bh);
    }
    void facingDeg;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // INVERTER  — wall-mount box with display + grille
  // ────────────────────────────────────────────────────────────────────────────
  private static renderInverter(
    body: Phaser.GameObjects.Graphics,
    detail: Phaser.GameObjects.Graphics | null,
    item: Item,
    px: number,
    py: number,
    w: number,
    h: number,
  ) {
    // Shadow
    body.fillStyle(0x000000, 0.5);
    body.fillRoundedRect(px + 3, py + 5, w, h, 5);
    // Body
    body.fillStyle(item.frame, 1);
    body.fillRoundedRect(px, py, w, h, 5);
    // Top edge highlight
    body.lineStyle(1, 0xffffff, 0.25);
    body.strokeRoundedRect(px + 1, py + 1, w - 2, h - 2, 4);
    // Inset face
    const pad = Math.max(4, Math.min(w, h) * 0.1);
    const ix = px + pad;
    const iy = py + pad;
    const iw = w - pad * 2;
    const ih = h - pad * 2;
    body.fillStyle(item.silicon, 1);
    body.fillRoundedRect(ix, iy, iw, ih, 3);

    if (detail) {
      // Display screen — glowing amber pixel
      const displayH = ih * 0.28;
      const displayW = iw * 0.7;
      const dx = ix + (iw - displayW) / 2;
      const dy = iy + ih * 0.12;
      detail.fillStyle(0x0a0d12, 1);
      detail.fillRoundedRect(dx, dy, displayW, displayH, 2);
      detail.fillStyle(0xfdbc1a, 0.92);
      detail.fillRect(dx + 4, dy + displayH * 0.5 - 1, displayW * 0.7, 2);
      // Grille / vents
      const grilleY = iy + ih * 0.55;
      const grilleH = ih * 0.32;
      const grilleW = iw * 0.7;
      const gx = ix + (iw - grilleW) / 2;
      detail.lineStyle(1, 0x9ab0d0, 0.45);
      const slits = 4;
      for (let i = 0; i < slits; i++) {
        const sy = grilleY + (i / (slits - 1)) * grilleH;
        detail.lineBetween(gx, sy, gx + grilleW, sy);
      }
      // Brand chip
      detail.fillStyle(0xdb1215, 0.95);
      detail.fillRect(ix + iw - 8, iy + 4, 4, 4);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // BATTERY  — stacked-cell rectangle with capacity bars
  // ────────────────────────────────────────────────────────────────────────────
  private static renderBattery(
    body: Phaser.GameObjects.Graphics,
    detail: Phaser.GameObjects.Graphics | null,
    item: Item,
    px: number,
    py: number,
    w: number,
    h: number,
  ) {
    body.fillStyle(0x000000, 0.5);
    body.fillRoundedRect(px + 3, py + 5, w, h, 6);
    body.fillStyle(item.frame, 1);
    body.fillRoundedRect(px, py, w, h, 6);
    body.lineStyle(1, 0xffffff, 0.2);
    body.strokeRoundedRect(px + 1, py + 1, w - 2, h - 2, 5);
    const pad = Math.max(4, Math.min(w, h) * 0.1);
    const ix = px + pad;
    const iy = py + pad;
    const iw = w - pad * 2;
    const ih = h - pad * 2;
    body.fillStyle(item.silicon, 1);
    body.fillRoundedRect(ix, iy, iw, ih, 3);

    if (detail) {
      // Capacity bars — three stacked horizontal segments along the long axis
      const horizontal = iw >= ih;
      const bars = 3;
      const margin = 4;
      if (horizontal) {
        const barH = (ih - margin * (bars + 1)) / bars;
        for (let i = 0; i < bars; i++) {
          const by = iy + margin + i * (barH + margin);
          detail.fillStyle(0x57c47a, 0.85);
          detail.fillRoundedRect(ix + margin, by, iw - margin * 2, barH, 2);
        }
      } else {
        const barW = (iw - margin * (bars + 1)) / bars;
        for (let i = 0; i < bars; i++) {
          const bx = ix + margin + i * (barW + margin);
          detail.fillStyle(0x57c47a, 0.85);
          detail.fillRoundedRect(bx, iy + margin, barW, ih - margin * 2, 2);
        }
      }
      // BAT badge
      detail.fillStyle(0xfdbc1a, 0.9);
      detail.fillRect(ix + iw - 14, iy + 2, 10, 4);
      // Subtle border
      detail.lineStyle(0.5, 0xffffff, 0.25);
      detail.strokeRoundedRect(ix + 0.5, iy + 0.5, iw - 1, ih - 1, 3);
    }
  }

  static lighten(hex: number, amount: number): number {
    const r = (hex >> 16) & 0xff;
    const g = (hex >> 8) & 0xff;
    const b = hex & 0xff;
    const mix = (c: number) => Math.min(255, Math.round(c + (255 - c) * amount));
    return (mix(r) << 16) | (mix(g) << 8) | mix(b);
  }
}
